import type { Ref } from 'react';

import { Flex } from '@bonusly/design-system';

import type { BizyUserMessageNode } from '../../../lib/bizy/types';
import { BizyRecognizeMomentChip } from './bizy-recognize-moment-chip';

// Render path for user-authored message nodes (BIZY-651). Mirrors the assistant
// DocumentRenderer's role on the user side, but intentionally minimal: a small
// switch over the (currently single-member) user node union. This is the
// generalization seam — add a case + renderer as new user node types appear.

function renderUserNode(node: BizyUserMessageNode, index: number, fontSize?: string | object) {
	switch (node.type) {
		case 'recognize_moment':
			return <BizyRecognizeMomentChip key={index} person={node.payload.person} moment={node.payload.moment} fontSize={fontSize} />;
		default:
			// Unknown/newer node type — render nothing rather than break the transcript.
			return null;
	}
}

export interface BizyUserMessageNodesProps {
	nodes: BizyUserMessageNode[];
	fontSize?: string | object;
	/** Attached to the root element so the chat view can anchor-scroll this turn. */
	anchorRef?: Ref<HTMLDivElement>;
}

// `anchorRef` and `data-user-message` mirror BizyUserMessage's root so both chat
// scroll controllers can anchor a node-rendered turn like any typed message (BIZY-678).
export function BizyUserMessageNodes({ nodes, fontSize, anchorRef }: BizyUserMessageNodesProps) {
	return (
		<Flex ref={anchorRef} direction="column" alignItems="flex-end" alignSelf="flex-end" gap={2} maxW="100%" data-user-message="true">
			{nodes.map((node, index) => renderUserNode(node, index, fontSize))}
		</Flex>
	);
}
